import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import moment from 'moment';
import { FilterMapping } from '../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { RequestFromBuilderResponse } from '../../../../form-builder/interface/request-form-builder.model';
import { ENDPOINTS } from '../../constants/endpoints.constants';
import { Option } from '../../models/option.model';
import {
  ApiObjectResponse,
  ApiResponse,
} from '../../models/api-response.model';
import {
  RequestPosition,
  RequestDetailsResponse,
  RequestStep,
} from '../../models/request-details.model';
import {
  CreateRequestSchedulePayload,
  RequestSchedule,
  RequestScheduleDetails,
} from '../../models/request-schedule.model';
import { RequestHistory } from '../../models/request-history.model';
import { RequestCard } from '../../models/request.model';
import { StepsDiagramTree } from '../../models/steps-diagram.model';
import { StepStatusEnum } from '../../enums/step-status';
import { WorkflowCategory } from '../../models/workflow-category.model';
import { SearchEnum } from '../../enums/search.enum';
import { IRequestService } from '../../interfaces/request-service.interface';
import { TenantService } from '../tenant/tenant.service';

@Injectable({
  providedIn: 'root',
})
export class VendorRequestService implements IRequestService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  stepStatusEnum = StepStatusEnum;

  showContributorsTab = signal<boolean>(false);
  showAddScheduleAction = signal<boolean>(false);

  detailsTabs = [
    {
      id: 3,
      key: 'requirements',
      name: this.transloco.translate(
        'request_details_screen_requirements_tab_label'
      ),
    },
    {
      id: 4,
      key: 'steps-diagram',
      name: this.transloco.translate(
        'request_details_screen_step_diagram_tab_label'
      ),
    },
  ];

  navigationPath(route: string) {
    return this.tenantService.vendorPath(route);
  }

  mapSummaryDetailsResponse(
    response: RequestDetailsResponse
  ): RequestDetailsResponse {
    return {
      ...response,
      data: {
        ...response.data,
        is_triggable: true,
        permissions: [{ action: 'own' }],
      },
    };
  }

  private addFiltersToParams(
    params: HttpParams,
    filters?: Record<string, string | string[]>
  ): HttpParams {
    if (!filters || Object.keys(filters).length === 0) return params;

    const processedFilters = this.expandResourceTypeFilter(filters);

    Object.entries(processedFilters).forEach(([key, value]) => {
      if (!this.hasValidValue(value)) return;

      if (key === SearchEnum.keyword) return;

      const isNonIndexedArrayFilter = key.endsWith('][]');

      if (isNonIndexedArrayFilter) {
        const items = Array.isArray(value)
          ? value
          : value
              .split(',')
              .map(i => i.trim())
              .filter(Boolean);
        items.forEach(item => {
          if (item?.trim()) {
            params = params.append(`filters[${key}`, item.trim());
          }
        });
      } else if (Array.isArray(value) || key.endsWith('][')) {
        const items = Array.isArray(value)
          ? value
          : value
              .split(',')
              .map(i => i.trim())
              .filter(Boolean);
        items.forEach((item, index) => {
          if (item?.trim()) {
            params = params.append(`filters[${key}${index}]`, item.trim());
          }
        });
      } else if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue) return;
        const formattedValue = this.formatFilterValue(key, trimmedValue);
        const filterKey = key.endsWith(']')
          ? `filters[${key}`
          : `filters[${key}]`;
        params = params.append(filterKey, formattedValue);
      }
    });

    return params;
  }

  private expandResourceTypeFilter(
    filters: Record<string, string | string[]>
  ): Record<string, string | string[]> {
    const resourceTypeValue = filters['resourceType'];
    if (!resourceTypeValue || typeof resourceTypeValue !== 'string') {
      return filters;
    }

    const expanded = { ...filters };
    delete expanded['resourceType'];

    const typeKey = FilterMapping.CURRENT_ASSIGNEES_TYPE;
    const idsKey = FilterMapping.CURRENT_ASSIGNEES_IDS;

    const parsed = JSON.parse(resourceTypeValue);
    if (parsed.resourceType) {
      expanded[typeKey] = parsed.resourceType;
    }
    if (parsed.assigneeId && parsed.assigneeId.length > 0) {
      expanded[idsKey] = Array.isArray(parsed.assigneeId)
        ? parsed.assigneeId.map(String)
        : [String(parsed.assigneeId)];
    }

    return expanded;
  }

  private hasValidValue(value: string | string[] | null | undefined): boolean {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'string' && value.trim().length > 0;
  }

  private formatFilterValue(key: string, value: string): string {
    if (!moment(value).isValid()) {
      return value;
    }

    const parsed = moment(value);
    const [year, month, day] = [parsed.year(), parsed.month(), parsed.date()];
    const isAfter =
      key === FilterMapping.CREATED_AFTER_REQUESTS ||
      key === FilterMapping.COMPLETED_AFTER_REQUESTS;
    const [hour, minute, second] = isAfter ? [0, 0, 0] : [23, 59, 59];

    return moment
      .utc([year, month, day, hour, minute, second])
      .format('YYYY-MM-DD HH:mm:ss');
  }

  getRequestList(
    page: number,
    per_page: number,
    categoryId: number,
    search: string
  ) {
    let params = new HttpParams();
    if (categoryId) {
      params = params.append('filters[category_id]', categoryId);
    }
    params = params.append('page', page).append('per_page', per_page);
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    params = params.append('sort[order][type]', 'asc');

    const url = ENDPOINTS.VendorPortal.FlowTemplates.List;
    return this.http.get<ApiResponse<RequestCard>>(url, {
      params,
    });
  }

  getRequestDetails(id: string): Observable<RequestDetailsResponse> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.Details.replace(
      '{id}',
      id
    );
    return this.http.get<RequestDetailsResponse>(url);
  }

  getRequestSummary(id: string): Observable<RequestDetailsResponse> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.Summary.replace(
      '{id}',
      id
    );
    return this.http.get<RequestDetailsResponse>(url);
  }

  triggerRequest(form: any, requestID: number): Observable<any> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.Trigger.replace(
      '{id}',
      requestID.toString()
    );
    return this.http.post(url, form);
  }

  saveRequest(
    form: any,
    stepId: number,
    options?: { url?: string }
  ): Observable<any> {
    const url =
      options?.url ??
      ENDPOINTS.VendorPortal.FlowTemplates.Save.replace(
        '{stepId}',
        stepId.toString()
      );
    return this.http.put(url, form);
  }

  getRequestFromBuilder(
    id: string,
    projectId: number,
    queryParams: any
  ): Observable<RequestFromBuilderResponse> {
    let params = new HttpParams();
    if (projectId) {
      params = new HttpParams().append('default_values[project_id]', projectId);
    }
    if (Object.keys(queryParams).length > 0) {
      Object.keys(queryParams).forEach(key => {
        params = params.append(`default_values[${key}]`, queryParams[key]);
      });
    }

    const url = ENDPOINTS.VendorPortal.FlowTemplates.FormSections.replace(
      '{id}',
      id
    );
    return this.http.get<RequestFromBuilderResponse>(url, { params });
  }

  getRequestHistory(
    id: string,
    page: number,
    per_page: number,
    filter: string,
    sort: { key: string; value: 'asc' | 'desc' },
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    if (filter && filter !== 'all') {
      params = params.append('filters[state]', filter);
    }

    if (sort.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }

    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (additionalFilters) {
      params = this.addFiltersToParams(params, additionalFilters);
    }

    const url = ENDPOINTS.VendorPortal.FlowTemplates.Flows.replace('{id}', id);
    return this.http.get<ApiResponse<RequestHistory>>(url, { params });
  }

  getRequestSchedules(
    id: string,
    page: number,
    per_page: number
  ): Observable<ApiResponse<RequestSchedule>> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    const url = ENDPOINTS.VendorPortal.FlowTemplates.Schedules.replace(
      '{id}',
      id
    );
    return this.http.get<ApiResponse<RequestSchedule>>(url, { params });
  }

  createRequestSchedule(
    id: string,
    payload: CreateRequestSchedulePayload
  ): Observable<any> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.Schedules.replace(
      '{id}',
      id
    );
    return this.http.post(url, payload);
  }

  updateRequestSchedule(
    requestId: string,
    scheduleId: string,
    payload: CreateRequestSchedulePayload
  ): Observable<any> {
    const url = `${ENDPOINTS.VendorPortal.FlowTemplates.Schedules.replace(
      '{id}',
      requestId
    )}/${scheduleId}`;
    return this.http.put(url, payload);
  }

  getRequestScheduleById(
    requestId: string,
    scheduleId: string
  ): Observable<ApiObjectResponse<RequestScheduleDetails>> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.ScheduleDetails.replace(
      '{id}',
      requestId
    ).replace('{scheduleId}', scheduleId);

    return this.http.get<ApiObjectResponse<RequestScheduleDetails>>(url);
  }

  cancelRequestSchedule(
    requestId: string,
    scheduleId: string
  ): Observable<unknown> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.CancelSchedule.replace(
      '{id}',
      requestId
    ).replace('{scheduleId}', scheduleId);

    return this.http.post(url, {});
  }

  getFlowStepsDetails(
    flowId: string | number,
    options?: { url?: string }
  ): Observable<any> {
    const url =
      options?.url ??
      ENDPOINTS.VendorPortal.FlowTemplates.FlowDetails.replace(
        '{flowId}',
        flowId.toString()
      );
    return this.http.get(url);
  }

  getFlowSteps(
    flowId: string | number,
    options?: { url?: string }
  ): Observable<any> {
    const url =
      options?.url ??
      ENDPOINTS.VendorPortal.Flow.Details.replace(
        '{flowId}',
        flowId.toString()
      );
    return this.http.get(url);
  }

  getFormSection(
    stepID: string | number,
    options?: { url?: string }
  ): Observable<any> {
    const url =
      options?.url ??
      ENDPOINTS.VendorPortal.Step.FormSections.replace(
        '{stepId}',
        stepID.toString()
      );
    return this.http.get(url);
  }
  takeStepAction(
    form: any,
    stepID: string | number,
    options?: { url?: string }
  ) {
    const url =
      options?.url ??
      ENDPOINTS.VendorPortal.Step.Assignees.replace(
        '{stepId}',
        stepID.toString()
      );
    return this.http.put(url, form);
  }

  /******************** view request flow **********************/
  getFlowDetails(id: string): Observable<RequestDetailsResponse> {
    const url = ENDPOINTS.VendorPortal.Flow.Details.replace('{flowId}', id);
    return this.http.get<RequestDetailsResponse>(url);
  }

  getFlowFormSections(id: string): Observable<RequestFromBuilderResponse> {
    const url = ENDPOINTS.VendorPortal.Flow.FormSections.replace(
      '{flowId}',
      id
    );
    return this.http.get<RequestFromBuilderResponse>(url);
  }

  /******************** steps diagram **********************/
  getStepsDiagramTree(id: string): Observable<StepsDiagramTree> {
    const url = ENDPOINTS.VendorPortal.FlowTemplates.StepTemplateTree.replace(
      '{id}',
      id
    );
    return this.http.get<StepsDiagramTree>(url);
  }

  getWorkflowCategories(): Observable<ApiResponse<WorkflowCategory>> {
    return of({
      data: [],
    } as unknown as ApiResponse<WorkflowCategory>);
  }

  exportWorkflow(form: any, id: number): Observable<HttpResponse<Blob>> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let headers = new HttpHeaders({});

    let params = new HttpParams();
    if (form.filters && form.filters !== 'all') {
      params = params.append('filters[state]', form.filters);
    }
    if (form.language) {
      headers = headers.append('Accept-Language', form.language);
    }
    if (form.type) {
      params = params.append('type', form.type);
    }
    params = params.append('timezone', timezone);

    const url = ENDPOINTS.VendorPortal.FlowTemplates.FlowExport.replace(
      '{id}',
      id.toString()
    );
    return this.http.get(url, {
      params,
      headers,
      responseType: 'blob',
      observe: 'response',
    });
  }

  holdRequest(form: any, flowId: number): Observable<any> {
    const url = ENDPOINTS.VendorPortal.Flow.Hold.replace(
      '{flowId}',
      flowId.toString()
    );
    return this.http.put(url, form);
  }

  resumeRequest(form: any, flowId: number): Observable<any> {
    const url = ENDPOINTS.VendorPortal.Flow.Resume.replace(
      '{flowId}',
      flowId.toString()
    );
    return this.http.put(url, form);
  }

  ////////////////////////////////////////////////////////
  getPositionState(position: RequestPosition): Option {
    // Handle single step case
    if (position.steps.length === 1) {
      return position.steps[0].details?.state ?? this.createEmptyOption();
    }

    // Handle multiple steps case
    if (position.steps.length > 1) {
      const stepCounts = this.getStepStatusCounts(position.steps);
      return this.determinePositionStateByPriority(stepCounts);
    }

    return this.createEmptyOption();
  }

  private getStepStatusCounts(steps: RequestStep[]): Record<string, number> {
    const counts: Record<string, number> = {};

    // Initialize all status counts to 0
    Object.values(this.stepStatusEnum).forEach(status => {
      counts[status] = 0;
    });

    // Count steps by status
    steps.forEach(step => {
      const status = step.details?.state?.value;
      if (status && Object.prototype.hasOwnProperty.call(counts, status)) {
        counts[status]++;
      }
    });

    return counts;
  }

  private determinePositionStateByPriority(
    stepCounts: Record<string, number>
  ): Option {
    // Define status priority order (highest to lowest priority)
    const statusPriority = [
      this.stepStatusEnum.ON_HOLD,
      this.stepStatusEnum.SENT_BACK,
      this.stepStatusEnum.INPROGRESS,
      this.stepStatusEnum.REJECTED,
      this.stepStatusEnum.SENT_FORWARD,
      this.stepStatusEnum.COMPLETED,
      this.stepStatusEnum.SKIPPED,
      this.stepStatusEnum.NOT_STARTED,
      this.stepStatusEnum.NO_ACTION,
    ];

    // Find the highest priority status that has at least one step
    for (const status of statusPriority) {
      if (stepCounts[status] > 0) {
        return this.createStatusOption(status);
      }
    }

    return this.createStatusOption(this.stepStatusEnum.NOT_STARTED);
  }

  private createStatusOption(status: string): Option {
    const translationKeys: Record<string, string> = {
      [this.stepStatusEnum.ON_HOLD]: 'step_status_on_hold',
      [this.stepStatusEnum.SENT_BACK]: 'step_status_sent_back',
      [this.stepStatusEnum.INPROGRESS]: 'step_status_in_progress',
      [this.stepStatusEnum.REJECTED]: 'step_status_rejected',
      [this.stepStatusEnum.SENT_FORWARD]: 'step_status_sent_forward',
      [this.stepStatusEnum.COMPLETED]: 'step_status_completed',
      [this.stepStatusEnum.SKIPPED]: 'step_status_skipped',
      [this.stepStatusEnum.NOT_STARTED]: 'step_status_not_started',
      [this.stepStatusEnum.NO_ACTION]: 'step_status_no_action',
    };

    return {
      value: status,
      label: this.transloco.translate(
        translationKeys[status] || 'step_status_not_started'
      ),
    };
  }

  private createEmptyOption(): Option {
    return { value: '', label: '' };
  }
}
